import { useState } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'

// Definindo os tipos para os dados
type Company = {
  name: string
  cnpj: string
  problem: string
  days: number
}

type MonthlyDetails = {
  contractSigned: Company[]
  pendingSignature: Company[]
  suspended: Company[]
  expiredCertificate: Company[]
}

type MonthlyStats = {
  total: number
  contractSigned: number
  pendingSignature: number
  suspended: number
  expiredCertificate: number
}

// Dados simulados organizados por mês
const monthlyDetails: Record<string, MonthlyDetails> = {
  January: {
    contractSigned: [
      { name: 'Empresa A', cnpj: '00.000.000/0001-00', problem: 'Nenhum', days: 0 },
      { name: 'Empresa B', cnpj: '11.111.111/0001-11', problem: 'Nenhum', days: 0 }
    ],
    pendingSignature: [
      { name: 'Empresa C', cnpj: '22.222.222/0001-22', problem: 'Contrato não assinado', days: 15 },
      { name: 'Empresa D', cnpj: '33.333.333/0001-33', problem: 'Contrato não assinado', days: 7 }
    ],
    suspended: [
      { name: 'Empresa E', cnpj: '44.444.444/0001-44', problem: 'Pagamento atrasado', days: 30 },
      { name: 'Empresa F', cnpj: '55.555.555/0001-55', problem: 'Violação de termos', days: 10 }
    ],
    expiredCertificate: [
      { name: 'Empresa G', cnpj: '66.666.666/0001-66', problem: 'Certificado expirado', days: 45 },
      { name: 'Empresa H', cnpj: '77.777.777/0001-77', problem: 'Certificado expirado', days: 20 }
    ]
  },
  February: {
    contractSigned: [],
    pendingSignature: [],
    suspended: [],
    expiredCertificate: []
  }
}

// Calcula as estatísticas com base nos dados mensais
const monthlyStats: Record<string, MonthlyStats> = Object.keys(monthlyDetails).reduce(
  (acc, month) => {
    const details = monthlyDetails[month]

    acc[month] = {
      total: Object.values(details).reduce((total, companyArray) => total + companyArray.length, 0),
      contractSigned: details.contractSigned.length,
      pendingSignature: details.pendingSignature.length,
      suspended: details.suspended.length,
      expiredCertificate: details.expiredCertificate.length
    }

    return acc
  },
  {} as Record<string, MonthlyStats>
)

type StatKey = keyof MonthlyStats

const StatCard = ({
  title,
  value,
  color,
  onClick,
  isSelected
}: {
  title: string
  value: number
  color: string
  onClick: () => void
  isSelected: boolean
}) => (
  <div
    className={`bg-background text-foreground p-4 rounded-lg shadow cursor-pointer hover:shadow-md transition-shadow ${
      isSelected ? 'ring-2 ring-offset-2 ring-' + color : ''
    }`}
    onClick={onClick}
  >
    <h3 className="text-lg font-semibold text-foreground">{title}</h3>
    <p className="text-3xl font-bold" style={{ color }}>
      {value}
    </p>
  </div>
)

export default function ResumoFinanceira() {
  const [selectedCategory, setSelectedCategory] = useState<StatKey | null>(null)
  const [selectedMonth, setSelectedMonth] = useState<keyof typeof monthlyStats>(
    Object.keys(monthlyStats)[0] as keyof typeof monthlyStats // Garantindo um mês padrão
  )

  const handleCategoryClick = (category: StatKey) => {
    if (category === 'total') {
      setSelectedCategory(null)
    } else {
      setSelectedCategory(category)
    }
  }

  const stats = monthlyStats[selectedMonth]
  const details = monthlyDetails[selectedMonth]

  return (
    <div className="space-y-8">
      {/* Seletor de mês */}
      <select
        className="p-2  rounded text-foreground bg-background shadow-md "
        value={selectedMonth}
        onChange={e => setSelectedMonth(e.target.value as keyof typeof monthlyStats)}
      >
        {Object.keys(monthlyStats).map(month => (
          <option key={month} value={month}>
            {month}
          </option>
        ))}
      </select>

      {stats ? (
        <>
          <Card className="shadow-md ">
            <CardHeader>
              <CardTitle className="text-foreground">Empresas Cadastradas em {selectedMonth}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4 shadow-md  ">
                <StatCard
                  title="Total de Empresas"
                  value={stats.total || 0}
                  color="#3498db"
                  onClick={() => handleCategoryClick('total')}
                  isSelected={selectedCategory === 'total'}
                />
                <StatCard
                  title="Contrato Assinado"
                  value={stats.contractSigned || 0}
                  color="#2ecc71"
                  onClick={() => handleCategoryClick('contractSigned')}
                  isSelected={selectedCategory === 'contractSigned'}
                />
                <StatCard
                  title="Pendentes de Assinatura"
                  value={stats.pendingSignature || 0}
                  color="#f39c12"
                  onClick={() => handleCategoryClick('pendingSignature')}
                  isSelected={selectedCategory === 'pendingSignature'}
                />
                <StatCard
                  title="Suspensas"
                  value={stats.suspended || 0}
                  color="#e74c3c"
                  onClick={() => handleCategoryClick('suspended')}
                  isSelected={selectedCategory === 'suspended'}
                />
                <StatCard
                  title="Certificado Expirado"
                  value={stats.expiredCertificate || 0}
                  color="#e67e22"
                  onClick={() => handleCategoryClick('expiredCertificate')}
                  isSelected={selectedCategory === 'expiredCertificate'}
                />
              </div>
            </CardContent>
          </Card>

          {selectedCategory && selectedCategory !== 'total' && details[selectedCategory]?.length > 0 && (
            <Card className=" mt-4">
              <CardHeader className="bg-[#e6f3ff]">
                <CardTitle className="text-foreground">
                  {selectedCategory === 'suspended' ? 'Empresas Suspensas' : 'Detalhes das Empresas'}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {details[selectedCategory].map((company, index) => (
                    <div key={index} className="bg-white p-4 rounded-lg shadow">
                      <h3 className="text-lg font-semibold text-foreground">{company.name}</h3>
                      <p className="text-sm ">CNPJ: {company.cnpj}</p>
                      {selectedCategory === 'suspended' ? (
                        <p className="text-sm text-[#e74c3c]">Dias com problema: {company.days}</p>
                      ) : (
                        <p className="text-sm text-[#f39c12]">Problema: {company.problem}</p>
                      )}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {selectedCategory && selectedCategory !== 'total' && details[selectedCategory]?.length === 0 && (
            <p className="text-red-500">Nenhum dado disponível para a categoria selecionada neste mês.</p>
          )}
        </>
      ) : (
        <p className="text-red-500">Nenhum dado disponível para o mês selecionado.</p>
      )}
    </div>
  )
}
